/**
 * Step policy for a stepped Creator: which steps are visible, where Next / Back go, what the step
 * header shows, and which binds and sections a step owns so Next can validate only that step.
 *
 * Pure: the Creator host passes in the compiled steps, a condition reader and the page, and renders
 * what comes back. Invariant: fewer than two visible steps is "not stepped", and the host must then
 * render exactly as a single-page Creator (no header, no Next / Back).
 */
import type { SurfaceContextLike } from '../contracts/surface.context.contracts';
import type { CompiledStep, CompiledSurfaceSchema } from '../runtime/compiled.form';
import { evaluateCondition } from '../runtime/rule.condition.evaluator';
import type { CreatorStepHeaderItem, CreatorStepNavigation } from './creator.steps.model';

/** The steps whose `showWhen` currently holds, in authored order. `read` is the handle's condition reader. */
export function visibleCreatorSteps(steps: readonly CompiledStep[] | undefined, read: (bind: string) => unknown): readonly CompiledStep[] {

  return (steps ?? []).filter(step => !step.showWhen || evaluateCondition(step.showWhen, read));
}

/**
 * Index of the current step among the visible ones. A step that has just been hidden under the user
 * falls back to the nearest visible step before it, so the user is never moved forward past a step
 * they have not seen.
 */
export function currentCreatorStepIndex(all: readonly CompiledStep[], visible: readonly CompiledStep[], currentId: string | null): number {

  if (!currentId || visible.length === 0) return 0;
  const index = visible.findIndex(step => step.id === currentId);
  if (index >= 0) return index;

  const authored = all.findIndex(step => step.id === currentId);
  for (let candidate = authored - 1; candidate >= 0; candidate -= 1) {
    const fallback = visible.findIndex(step => step.id === all[candidate].id);
    if (fallback >= 0) return fallback;
  }
  return 0;
}

/** What the footer offers on the current step. */
export function creatorStepNavigation(visible: readonly CompiledStep[], index: number): CreatorStepNavigation {

  const stepped = visible.length > 1;
  const first = index <= 0;
  const last = index >= visible.length - 1;
  return {
    stepped,
    first,
    last,
    nextId: stepped && !last ? visible[index + 1].id : null,
    previousId: stepped && !first ? visible[index - 1].id : null,
  };
}

/**
 * The step header: steps behind the user are done, the current one is highlighted, and a skipped step
 * marked `keepInHeader` stays in place as "Not needed". Empty when fewer than two steps would show.
 */
export function creatorStepHeader(all: readonly CompiledStep[], visible: readonly CompiledStep[], index: number): readonly CreatorStepHeaderItem[] {

  const shown = all.filter(step => step.keepInHeader || visible.includes(step));
  if (shown.length < 2) return [];
  return shown.map((step, position) => ({
    id: step.id,
    label: step.label,
    number: position + 1,
    state: stepState(visible.indexOf(step), index),
  }));
}

/** Every section path the step renders, nested sections included. */
export function creatorStepSectionPaths(compiled: Pick<CompiledSurfaceSchema, 'sections'>, step: CompiledStep): readonly string[] {

  return compiled.sections
    .map(section => section.path)
    .filter(path => isWithinStep(path, step));
}

/** The binds of every field occurrence the step renders. Repeat rows are validated by submit, not by Next. */
export function creatorStepBinds(compiled: Pick<CompiledSurfaceSchema, 'fieldInstances'>, step: CompiledStep): readonly string[] {

  const binds = compiled.fieldInstances
    .filter(instance => !instance.displayOnly && instance.sectionPath !== undefined && isWithinStep(instance.sectionPath, step))
    .map(instance => instance.bind);
  return [...new Set(binds)];
}

/**
 * Next's gate. Marks every visible field of the step touched so its errors show, then reports whether
 * the step may be left: no visible field invalid or still validating, and no section-level error.
 */
export function revealCreatorStepErrors(page: Pick<SurfaceContextLike, 'field' | 'section'>, binds: readonly string[], sectionPaths: readonly string[]): boolean {

  let ok = true;
  for (const bind of binds) {
    const field = page.field(bind);
    if (field.$hidden()) continue;
    field.markTouched();
    if (!field.$valid() || field.$validating()) ok = false;
  }
  for (const path of sectionPaths) {
    const section = page.section(path);
    if (!section.$hidden() && section.$errors().length > 0) ok = false;
  }
  return ok;
}

function stepState(position: number, current: number): CreatorStepHeaderItem['state'] {

  if (position < 0) return 'skipped';
  if (position < current) return 'done';
  if (position === current) return 'current';
  return 'upcoming';
}

function isWithinStep(path: string, step: CompiledStep): boolean {

  return step.sections.some(section => path === section || path.startsWith(`${section}.`));
}

/**
 * The footer's step caption: "Step 2 of 3", plus the step's own status text when its `status` bind
 * reads a non-empty string ("Step 2 of 2 · 1 task can't fit this site"). Empty when not stepped.
 */
export function creatorStepCaption(visible: readonly CompiledStep[], index: number, read: (bind: string) => unknown): string {

  if (visible.length < 2) return '';
  const position = `Step ${index + 1} of ${visible.length}`;
  const bind = visible[index]?.status;
  const status = bind ? read(bind) : undefined;
  return typeof status === 'string' && status.trim() ? `${position} · ${status.trim()}` : position;
}
